import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { ResponsiveContainer, Legend, PieChart as RePieChart, Pie, Cell, Label, Sector } from 'recharts';
import { defaultNotifier } from 'functions/notificationHandler';
import Card from 'components/Detail/Card';
import { isEmpty, isEqual } from 'lodash';
import {
  GROUP_BY_UNIQUE_COLORS,
  getPieChartConfig,
  getActualTimeRange,
  chartGradientDefs,
  legendPayload,
  legendContent,
  formatYAxisLabel,
  getChartRenderRequirements,
  roundData,
  safeParseNumber,
  normalizePercentPieData,
  getThemeThenRandomSeriesColor,
  chartDotMenu
} from './utils'
import classNames from 'classnames';
import { ChartLoader } from './ChartLoader';
import { THEME_COLOR_SWATCH } from 'utils/constants'
import ChartDotMenu from './ChartDotMenu';
import { rawChartData, previewRawChartData } from '../../../../../api/dashboard';
import { CHART_DEFAULT_VALUES } from '../chartConfigurationsV2'
import { BLOCK, WARN } from './constants'

class PieChart extends Component {
  constructor(props) {
    super(props);
    this.state = {
      chartData: {
        groupBy: [],
        data: []
      },
      metricColumns: [],
      validData: false,
      dataErrorMessage: '',
      dataWarnMessage: '',
      uniqueGroupByValue: {},
      availableColorIndices: Array.from(Array(GROUP_BY_UNIQUE_COLORS.length).keys()),
      uniqueGroupByOriginalKey: {},
      isChartDataLoading: false,
      chartConfig: { ...CHART_DEFAULT_VALUES },
      hasMenu: false,
      activeIndex: -1,
    }
    this.leaveTimeout = null
  }

  onPieEnter = (_, index) => {
    if (this.leaveTimeout) {
      clearTimeout(this.leaveTimeout)
      this.leaveTimeout = null
    }
    this.setState({ activeIndex: index })
  }

  onPieLeave = () => {
    // Grace period to avoid blink when crossing tiny gaps between slices
    if (this.leaveTimeout) {
      clearTimeout(this.leaveTimeout)
    }
    this.leaveTimeout = setTimeout(() => {
      this.setState({ activeIndex: -1 })
      this.leaveTimeout = null
    }, 120)
  }

  onPieMove = (_, index) => {
    // Continuously update the active slice while moving within the chart for smoother hover
    if (typeof index === 'number' && index >= 0) {
      this.setState({ activeIndex: index })
    }
  }

  // eslint-disable-next-line
  componentWillReceiveProps(nextprops) {
    if (!isEqual(this.props.isDashboardInitialized, nextprops.isDashboardInitialized)) {
      if (nextprops.isPreview) {
        return this.setPreviewData()
      }
      if (nextprops.isDashboardInitialized) {
        return this.setData()
      }
    }

    if (nextprops.errors) {
      defaultNotifier(nextprops.errors, this.props);
    }
  }

  async componentDidMount() {
    const isOldChartConfig = typeof this.props.chart?.configuration === 'object' && this.props.chart?.configuration.hasOwnProperty('basicFields')
    this.setState({ hasMenu: !isOldChartConfig })

    if (!this.props.isPreview && !isOldChartConfig) {
      const chartConfig = JSON.parse(this.props.chart?.configuration)
      // For Pie charts at dashboard level, tiers/thresholds are not applicable. Sanitize loaded config.
      const sanitized = {
        ...chartConfig,
        colorTiers: { customConfigs: [] },
        enableThreshold: false,
        allThresholdType: 'absolute'
      }
      this.setState({ chartConfig: sanitized })//, () => { console.log('After parsing and saving - this.state.chartConfig: ', this.state.chartConfig)})
    }

    if (this.props.groupByColors) {
      this.setState({
        uniqueGroupByValue: { ...this.props.groupByColors }
      }, () => {
        const newChartData = this.state.chartData?.data?.map(metric => {
          return {
            ...metric,
            fill: this.getUniqueGroupByValue(metric.group)
          }
        })
        this.setState({
          chartData: newChartData
        })
      })
    }

    if (this.props.isPreview) {
      // For Pie charts, tiers/thresholds are not applicable. Sanitize preview values.
      const sanitized = {
        ...this.props.chartValues,
        colorTiers: { customConfigs: [] },
        enableThreshold: false,
        allThresholdType: 'absolute'
      }
      this.setState({ chartConfig: sanitized, hasMenu: false }, () => {
        this.setPreviewData()
      })
    }
    // else {
    //   this.setData()
    // }
  }

  async componentDidUpdate(prevProps) {
    if (!isEqual(this.props.devicesData, prevProps.devicesData)) {
      return this.setPreviewData()
    }

    if (this.props.isPreview) {

      const currentChartValues = this.props.chartValues
      const prevChartValues = prevProps.chartValues

      if (!isEqual(this.props.chartType, prevProps.chartType)) {

        return this.setPreviewData()
      }

      if (!isEqual(currentChartValues, prevChartValues)) {
        const sanitized = {
          ...currentChartValues,
          colorTiers: { customConfigs: [] },
          enableThreshold: false,
          allThresholdType: 'absolute'
        }
        this.setState({ chartConfig: sanitized }, () => {
          const currentDataStreams = this.props.dataStreams
          const prevDataStreams = prevProps.dataStreams

          if (!isEqual(currentDataStreams, prevDataStreams)) {
            return this.setPreviewData()
          }

          if (!isEqual(currentChartValues.devices, prevChartValues.devices)) {
            return this.setPreviewData()
          }

          if (!isEqual(currentChartValues.groupBy, prevChartValues.groupBy)) {
            return this.setPreviewData()
          }
        })
      }


      const currentDataStreams = this.props.dataStreams
      const prevDataStreams = prevProps.dataStreams

      if (!isEqual(currentDataStreams, prevDataStreams)) {
        return this.setPreviewData()
      }

      const currentChart = this.props.chart
      const prevChart = prevProps.chart
      const currentPreviewParams = this.props.previewParams
      const prevPreviewParams = prevProps.previewParams

      if (!isEqual(currentChart, prevChart) || !isEqual(currentPreviewParams, prevPreviewParams)) {
        return this.setPreviewData()
      }

      if (this.props.refreshPreview) {
        return this.setState({
          chartConfig: this.props.chartValues
        })
      }
    }

    const prevIsDashboardInitialized = prevProps.isDashboardInitialized
    const actualIsDashboardInitialized = this.props.isDashboardInitialized
    if ((prevIsDashboardInitialized !== actualIsDashboardInitialized) && actualIsDashboardInitialized) {
      this.setData()
    }
  }

  setPreviewData = async () => {
    const { chart, previewParams, dashboardTimeRange, isPreview } = this.props

    const actualTimeRange = getActualTimeRange(chart, dashboardTimeRange, isPreview)

    try {
      // const { hasBlockingIssues } = this.checkChartRenderRequirements(actualTimeRange)

      // if (hasBlockingIssues) {
      //   return this.setState({ isChartDataLoading: false })
      // }

      // Always use new version raw data flow
      this.setRawChartData()
    } catch (error) {
      return this.setState({
        chartData: {
          groupBy: [],
          data: []
        },
        dataErrorMessage: error.message,
        validData: false
      })
    }
  }

  setData = async () => {
    const { chart, dashboardTimeRange, dashboardDevices } = this.props
    this.setState({ isChartDataLoading: true })

    const actualTimeRange = getActualTimeRange(chart, dashboardTimeRange)

    try {
      // const { hasBlockingIssues } = this.checkChartRenderRequirements(actualTimeRange)

      // if (hasBlockingIssues) {
      //   return this.setState({ isChartDataLoading: false })
      // }

      // Always use new version raw data flow
      this.setRawChartData()
    } catch (error) {
      this.setState({ isChartDataLoading: false })
      return this.setState({
        chartData: {
          groupBy: [],
          data: []
        },
        dataErrorMessage: error.message,
        validData: false
      })
    }
  }

  checkChartRenderRequirements = (timeRange) => {
    const requirements = getChartRenderRequirements(this.state.chartConfig, timeRange)

    const warnMessages = requirements[WARN]?.map(r => r.message)?.join('\n')
    const blockMessages = requirements[BLOCK]?.map(r => r.message)?.join('\n')
    const hasWarningIssues = requirements[BLOCK]?.length > 0
    const hasBlockingIssues = requirements[BLOCK]?.length > 0

    if (hasWarningIssues) {
      this.setState({ dataWarnMessage: warnMessages })
    }

    if (hasBlockingIssues) {
      this.setState({
        validData: false,
        dataErrorMessage: blockMessages,
        chartData: { groupBy: [], data: [] }
      })
    }

    return {
      hasBlockingIssues
    }
  }

  setChartData = (data, chart) => {
    const { groupBy, metricsData, metricColumns } = data

    const chartConfig = getPieChartConfig(chart.configuration)

    const chartData = {
      groupBy: [...groupBy],
      data: [...metricsData]
    }

    if (isEmpty(chartData.groupBy)) {
      chartData.data = metricColumns.map(metric => {
        const res = {
          metric: metric,
          name: metric,
          value: 0,
          group: {}
        }
        if (isEmpty(metricsData)) {
          return res
        }
        const metricData = metricsData[0]
        res.value = safeParseNumber(metricData.values[metric]) || 0
        res.group = isEmpty(metricData.group) ? {} : { ...metricData.group }
        return res
      })
    }

    const validGroupBy = chartData.groupBy
      .filter(item => item !== 'time')[0] || ''

    if (validGroupBy) {
      chartData.data = chartData.data.map(metric => {
        const name = Object.values(metric.group)[0]
        const value = safeParseNumber(Object.values(metric.values)[0])
        return {
          ...metric,
          name: name,
          metric: name,
          value: value,
          fill: this.getUniqueGroupByValue(metric.group)
        }
      })
      // Do NOT sort slices alphabetically; keep original order so color indices align with stream order
      // chartData.data.sort((a, b) => a.name?.localeCompare(b, undefined, { sensitivity: 'base' }))
    } else {
      chartData.data = chartData.data.map((metric, index) => {
        return { ...metric }
      })
      this.setState({
        uniqueGroupByValue: {},
        availableColorIndices: Array.from(Array(GROUP_BY_UNIQUE_COLORS.length).keys())
      }, () => {
        if (this.props.onBarColorChange) {
          this.props.onBarColorChange(this.state.uniqueGroupByValue)
        }
      })
    }

    return this.setState({
      chartData: chartData,
      metricColumns: metricColumns,
      chartConfig: chartConfig,
      dataErrorMessage: '',
      validData: true
    })
  }

  renderPieLabel = (props) => {
    const { chartConfig, activeIndex } = this.state
    const { cx, cy, midAngle, innerRadius, outerRadius, percent, index, value, name, payload } = props

    // Render label only for the hovered/active slice unless always-on labels are enabled
    if (activeIndex !== index && !chartConfig?.enablePieChartLabels) return null

    const fixedDecimal = chartConfig.decimal || 0
    const RADIAN = Math.PI / 180
    const SIN = Math.sin(-RADIAN * midAngle)
    const COS = Math.cos(-RADIAN * midAngle)

    const radius = innerRadius + (outerRadius - innerRadius) * 0.5
    const x = cx + radius * COS
    const y = cy + radius * SIN

    // Always center the hover labels horizontally by the number position
    const textAnchor = 'middle'

    const displayPercent = chartConfig.labelDisplay === 'percent'

    let textValue = displayPercent ? `${(percent * 100).toFixed(fixedDecimal)}%` : formatYAxisLabel(value, chartConfig.format, fixedDecimal)
    textValue = `${chartConfig.prefix} ${textValue} ${chartConfig.suffix}`

    // Determine section title:
    // - compare_streams: use streamLabel from the configured streams
    // - by_category: use the slice name/metric (category value) so labels match legends
    // - default: fall back to streamLabel when present
    const isCompareStreams = chartConfig?.dataStreamSingle?.breakdownType === 'compare_streams'
    const isByCategory = chartConfig?.dataStreamSingle?.breakdownType === 'by_category'

    const streamLabel = (isCompareStreams
      ? chartConfig?.dataStreamSingle?.dataStreams?.streams?.[index]?.streamLabel?.value
      : chartConfig?.dataStreams?.streams?.[index]?.streamLabel?.value) || ''

    const categoryLabel = (typeof name === 'string' && name.trim().length > 0)
      ? name
      : (payload && typeof payload?.metric === 'string' ? payload.metric : '')

    const headerLabel = isByCategory ? categoryLabel : streamLabel

    return (
      <text
        x={x}
        y={y}
        textAnchor={textAnchor}
        pointerEvents="none"
        dominantBaseline="central"
        className='charts-no-cartesian__pie-label'
      >
        {headerLabel && (
          <tspan x={x} dy={-14} className='charts-no-cartesian__pie-label charts-no-cartesian__pie-label1' pointerEvents="none">
            {headerLabel}
          </tspan>
        )}
        <tspan x={x} dy={headerLabel ? 18 : 0} className='charts-no-cartesian__pie-label charts-no-cartesian__pie-label2' pointerEvents="none">
          {textValue}
        </tspan>
      </text>
    )
  }

  // Prevent unnecessary re-renders (e.g., from parent scroll)
  shouldComponentUpdate(nextProps, nextState) {
    return (
      !isEqual(this.props.height, nextProps.height) ||
      !isEqual(this.props.chartType, nextProps.chartType) ||
      !isEqual(this.props.chartValues, nextProps.chartValues) ||
      !isEqual(this.props.dataStreams, nextProps.dataStreams) ||
      !isEqual(this.props.previewParams, nextProps.previewParams) ||
      !isEqual(this.props.devicesData, nextProps.devicesData) ||
      !isEqual(this.props.chart, nextProps.chart) ||
      this.props.isEditMode !== nextProps.isEditMode ||
      !isEqual(this.state.chartData, nextState.chartData) ||
      !isEqual(this.state.chartConfig, nextState.chartConfig) ||
      this.state.isChartDataLoading !== nextState.isChartDataLoading ||
      this.state.activeIndex !== nextState.activeIndex
    );
  }

  getUniqueGroupByValue = (uniqueGroupValue) => {
    let availableIndices = [...this.state.availableColorIndices]
    let uniqueValue = ''
    let uniqueOriginalValue = Object.entries(uniqueGroupValue)
      .filter(([key, value]) => key !== 'time')
      .map(([key, value]) => value)[0]
    const uniqueValueColors = { ...this.state.uniqueGroupByValue }

    if (availableIndices.length === 0) {
      availableIndices = Array.from(Array(GROUP_BY_UNIQUE_COLORS.length).keys())
    }

    if (uniqueOriginalValue && (typeof (uniqueOriginalValue) === 'string')) {
      uniqueValue = uniqueOriginalValue.replace(/\s+/g, '_').replace(/[^\w_]/g, '')
      this.setState({
        uniqueGroupByOriginalKey: {
          ...this.state.uniqueGroupByOriginalKey,
          [uniqueValue]: uniqueOriginalValue
        }
      })
    }

    if (!uniqueValueColors.hasOwnProperty(`${uniqueValue}`)) {
      const colorIndex = availableIndices.shift()
      uniqueValueColors[`${uniqueValue}`] = getThemeThenRandomSeriesColor({
        key: uniqueOriginalValue || uniqueValue,
        index: colorIndex,
        palette: THEME_COLOR_SWATCH
      })
      this.setState({
        uniqueGroupByValue: uniqueValueColors,
        availableColorIndices: availableIndices
      }, () => {
        if (this.props.onBarColorChange) {
          this.props.onBarColorChange(this.state.uniqueGroupByValue)
        }
      })
      return uniqueValueColors[uniqueValue]
    }
    return uniqueValueColors[uniqueValue] || ''
  }

  getDeviceList = () => {
    const { chartValues, devicesData } = this.props

    if (chartValues?.devices && !!chartValues.devices.length) {
      return chartValues.devices
    }

    return devicesData?.map(item => item.name)
  }

  setRawChartData = async () => {
    const { chart, dashboardTimeRange, isPreview, isNewVersion, chartValues, isDefaultTime } = this.props
    const { chartConfig } = this.state;

    const actualTimeRange = getActualTimeRange(chart, dashboardTimeRange, isPreview, true, chartConfig)

    // Show loader while preparing data
    this.setState({ isChartDataLoading: true })

    // Show loader while preparing data
    this.setState({ isChartDataLoading: true })

    if (chartConfig.dataStreamSingle.breakdownType === 'compare_streams') {
      // Ensure every configured stream has at least one metric selected
      const streams = chartConfig?.dataStreamSingle?.dataStreams?.streams || chartConfig?.dataStreams?.streams || []
      const anyMissingMetrics = !streams.every(s => Array.isArray(s?.metric?.metrics) && s.metric.metrics.length > 0)
      if (anyMissingMetrics) {
        if (!this.props.isPreview) {
          // Suppress error on dashboard initial load
          return
        }
        return this.setState({
          chartData: {
            groupBy: [],
            data: []
          },
          dataErrorMessage: "Select a metric to display the preview",
          validData: false,
          isChartDataLoading: false
        })
      }
    }

    if (chartConfig.dataStreamSingle.breakdownType === 'by_category') {
      const hasMetrics = Array.isArray(chartConfig?.dataStreamSingle?.metric?.metrics) && chartConfig.dataStreamSingle.metric.metrics.length > 0
      if (!hasMetrics) {
        if (!this.props.isPreview) {
          // Suppress error on dashboard initial load
          return
        }
        return this.setState({
          chartData: {
            groupBy: [],
            data: []
          },
          dataErrorMessage: "Select a metric to display the preview",
          validData: false,
          isChartDataLoading: false
        })
      }
    }

    // if (chartValues?.devices?.length === 0) {
    //   return this.setState({
    //     chartData: {
    //       groupBy: [],
    //       data: []
    //     },
    //     dataErrorMessage: "Error: No devices selected",
    //     validData: false
    //   })
    // }

    if (this.props.sqlError) {
      return this.setState({
        chartData: {
          groupBy: [],
          data: []
        },
        dataErrorMessage: this.props.sqlError,
        validData: false
      })
    }

    let data = {}

    const devices = this.getDeviceList();

    if (this.props.isPreview) {
      if (this.props.dataStreams[0]) {
        this.setState({ isChartDataLoading: true })

        if (!devices.length) {
          return this.setState({ isChartDataLoading: false })
        }

        const response = await previewRawChartData({
          dataStreams: this.props.dataStreams,
          visoDevices: devices,
          dashboardId: this.props.dashboardId
        })
        data = {
          errors: response.data.errors || null,
          data: response.data.data.previewRawChartData
        }

        this.setState({ isChartDataLoading: false })
      }

      this.setState({
        dataErrorMessage: "Select a metric to display the preview"
      })

    } else {
      this.setState({ isChartDataLoading: true })

      if (devices.length) {
        if (isDefaultTime) {
          const response = await rawChartData({
            chartId: this.props.chart?.id,
            visoDevices: this.props.deviceIds?.length ? this.props.deviceIds : devices,
            timeRange: { start: actualTimeRange.start, end: actualTimeRange.end }
          })
          this.setState({ isChartDataLoading: false })
          data = {
            errors: response.data.errors || null,
            data: response.data.data.rawChartData
          }
        } else {
          const response = await rawChartData({
            chartId: this.props.chart?.id,
            visoDevices: this.props.deviceIds?.length ? this.props.deviceIds : devices,
          })
          this.setState({ isChartDataLoading: false })
          data = {
            errors: response.data.errors || null,
            data: response.data.data.rawChartData
          }
        }
      }

    }

    if (data.errors) {
      const errorMessage = data.errors?.[0]?.message
      return this.setState({
        dataErrorMessage: errorMessage,
        validData: false,
        isChartDataLoading: false
      })
    }

    if (data.data) {
      const rawData = data.data.flatMap(item => item.result.data)
      const rawFields = data.data.flatMap(item => item.result.fields)


      let chartData = {};
      if (chartConfig.dataStreamSingle.breakdownType === 'compare_streams') {
        const streams = chartConfig?.dataStreamSingle?.dataStreams?.streams || chartConfig?.dataStreams?.streams || []
        chartData = {
          groupBy: rawFields,
          data: rawData.map((metric, index) => {
            const stream = streams[index] || {}
            const metricKey = (Array.isArray(stream?.metric?.metrics) && stream.metric.metrics.length > 0)
              ? stream.metric.metrics.join(', ')
              : Object.keys(metric)[0]

            return {
              name: metricKey,
              metric: metricKey,
              value: Number(metric[rawFields[index]]) || 0,
              group: rawFields[index]
            };
          })
        };
      }

      if (chartConfig.dataStreamSingle.breakdownType === 'by_category') {
        const categoryField = rawFields[0];
        const metricField = rawFields[1];
        chartData = {
          groupBy: rawFields,
          data: rawData.map((metric, index) => {
            return {
              name: metric[categoryField], // name for legend/labels (e.g., "Claremont", "Rockingham")
              metric: metric[categoryField],
              value: Number(metric[metricField]) || 0,
              group: categoryField
            };
          })
        }
      };

      this.setState({
        chartData: {
          groupBy: chartData.groupBy,
          data: chartData.data,
          uniqueGroupValues: []
        },
        metricColumns: rawFields,
        dataErrorMessage: '',
        validData: true,
        isChartDataLoading: false,
        // cartesianConfig: mapChartValuesToCartesianConfig(this.state.chartConfig)
      })//, () => { console.log('After changes: ', this.state.cartesianConfig) })
    }
  }

  getOverrideData = (data) => {
    const { chartData, chartConfig } = this.state

    if (chartData.data.length === 0) {
      return ''
    }

    if (chartConfig?.colorTiers?.customConfigs?.length > 0 && chartConfig?.enableThreshold) {

      const tiers = chartConfig.colorTiers.customConfigs;
      let labelText = tiers[0].text;
      let configData = tiers[0];

      for (const tier of tiers) {
        if (data > parseFloat(tier.value)) {
          labelText = tier.text;
          configData = tier;
        }
      }

      return configData

    }

    // const dataKey = Object.keys(chartData.data[0].values)[0]
    // const firstItem = chartData.data[0]
    // const values = firstItem?.values
    // const dataKey = values
    //   ? Object.keys(values)[0]
    //   : Object.keys(firstItem)[0]

    // const overrideValue = cardConfig?.dataOverrides[dataKey]?.value

    // if (overrideValue) {
    //   return overrideValue
    // }
    return null

  }

  renderActiveShape = (props) => {
    const RADIAN = Math.PI / 180
    const { chartConfig } = this.state
    const {
      cx,
      cy,
      midAngle,
      innerRadius,
      outerRadius,
      startAngle,
      endAngle,
      fill,
      payload,
      percent,
      value
    } = props

    const fixedDecimal = chartConfig.decimal || 0

    const sin = Math.sin(-RADIAN * midAngle)
    const cos = Math.cos(-RADIAN * midAngle)
    const sx = cx + (outerRadius + 10) * cos
    const sy = cy + (outerRadius + 10) * sin
    const mx = cx + (outerRadius + 14) * cos
    const my = cy + (outerRadius + 14) * sin
    const ex = mx + (cos >= 0 ? 1 : -1) * 22
    const ey = my
    const textAnchor = cos >= 0 ? 'start' : 'end'

    const textValue = `${chartConfig.prefix} ${formatYAxisLabel(value, chartConfig.format, chartConfig.decimal)} ${chartConfig.suffix}`

    return (
      <g>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={outerRadius + 6}
          outerRadius={outerRadius + 10}
          fill={fill}
        />
        <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill="none" />
        <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
        <text x={ex + (cos >= 0 ? 1 : -1) * 12} y={ey} textAnchor={textAnchor} fill="#333" className='charts-no-cartesian__pie-label1'>{`${textValue}`}</text>
        <text x={ex + (cos >= 0 ? 1 : -1) * 12} y={ey} dy={18} textAnchor={textAnchor} fill="#999" className='charts-no-cartesian__pie-label2'>
          {`${(percent * 100).toFixed(0)}%`}
        </text>
      </g>
    )
  }

  renderInactiveShape = (props) => {
    const {
      cx,
      cy,
      innerRadius,
      outerRadius,
      startAngle,
      endAngle,
      fill,
    } = props

    return (
      <Sector
        // style={{ filter: "drop-shadow( 3px 3px 2px rgba(0, 0, 0, .7))" }}
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
    )
  }

  getErrorMessage = () => {
    const {chartType, chartValues} = this.props;

    if (!this.state.validData) {
      return this.state.dataErrorMessage
    }

    if((chartType === 'pie' || chartType === 'gauge' || chartType === 'doughnut') && chartValues.dataStreamSingle?.breakdownType === 'by_category' && !chartValues.dataStreamSingle?.groupBy?.metric?.length){
      return "Select a group by to display the chart"
    }

    return ''
  }

  // Render error message
  renderErrorContext = () => {
    return (
      <div className='chart-error'>
        <div>
          {this.getErrorMessage()}
        </div>
        <div>
          {this.state.dataWarnMessage}
        </div>
      </div>
    )
  }

  hasError = () => {
    const {chartType, chartValues} = this.props;

    if (!this.state.validData) {
      return true
    }

    if((chartType === 'pie' || chartType === 'gauge' || chartType === 'doughnut') && chartValues.dataStreamSingle?.breakdownType === 'compare_streams'){
      return false;
    }

    if((chartType === 'pie' || chartType === 'gauge' || chartType === 'doughnut') && chartValues.dataStreamSingle?.breakdownType === 'by_category' && !chartValues.dataStreamSingle?.groupBy?.metrics?.length){
      return true;
    }

    return false
  }

  renderContent = () => {
    const { chart, height } = this.props
    const { chartData, metricColumns, validData, dataErrorMessage, chartConfig, dataWarnMessage } = this.state

    const formattedChartData = Array.isArray(chartData?.data) && chartData.data.map((d) => {
      return {
        ...d,
        bucketed_time: d.bucketed_time
      };
    });

    const baseRoundedData = roundData(formattedChartData, chartConfig.decimal);
    const processedChartData = (chartConfig.format === 'percent')
      ? normalizePercentPieData(baseRoundedData)
      : baseRoundedData;

    const isV2 = !!chartConfig && Object.prototype.hasOwnProperty.call(chartConfig, 'version')
    const titleText = (isV2 ? chartConfig?.chartTitle : (this.props.title || chartConfig?.chartTitle || this.props.chart?.title))
    
    const normalizeKey = (value) => {
      if (typeof value === 'string') return value;
      if (value == null) return '';
      return String(value);
    }

    return (
      <React.Fragment>
        {
          this.hasError()
            ?
            this.renderErrorContext()
            :
            <div className=''>
              <div className='grid-row'>
                <div className='width-100'>
                  <div className='grid-col'>
                    <div className='job-training__title job-training__title2'>{titleText}</div>
                  </div>
                </div>

                <div className='job-training__charts2 job-training__chart_container'>
                  <ResponsiveContainer width="100%" height={height}>
                    <RePieChart margin={{ top: 0, right: 20, left: 20, bottom: 24 }} height={height}>


                      {/* Gradient definitions */}
                      {processedChartData.map((entry, index) => {
                        const stroke = this.getStrokeLine(entry, index);
                        const rawKey = normalizeKey(entry?.metric || entry?.name || entry?.group);
                        const fillId = `${this.props.chart.id}_${rawKey.replace(/\s+/g, '')}_${index}`
                        return chartGradientDefs(fillId, stroke)
                      })}

                      <Pie
                        data={processedChartData}
                        labelLine={false}
                        activeIndex={this.state.activeIndex}
                        // activeShape={this.renderActiveShape}
                        // inactiveShape={this.renderInactiveShape}
                        // label={chartConfig?.enablePieChartLabels ? this.renderPieLabel : null}
                        label={this.renderPieLabel}
                        dataKey="value"
                        cx="50%"
                        cy="50%"
                        outerRadius="80%"
                        paddingAngle={0}
                        stroke="none"
                        strokeWidth={0}
                        strokeLinejoin="round"
                        isAnimationActive={false}
                        rootTabIndex={33}
                        // Use Cell-level onMouseEnter for stable hover without excessive rerenders
                        onMouseLeave={this.onPieLeave}
                      >
                        {processedChartData.map((entry, index) => {
                          const rawKey = normalizeKey(entry?.metric || entry?.name || entry?.group);
                          const fillId = `${this.props.chart.id}_${rawKey.replace(/\s+/g, '')}_${index}`

                          return (
                            <Cell
                              key={`cell-${index}`}
                              fill={`url(#${fillId}Fill)`}
                              stroke="#ffffff"
                              strokeWidth={2}
                              onMouseEnter={() => this.onPieEnter(null, index)}
                            />
                          )
                        })}
                        {chartConfig.enableDataTitle &&
                          <Label
                            position="center"
                            value={chartConfig.dataTitle}
                            className='charts-no-cartesian__doughnut-label'
                          />
                        }
                      </Pie>

                      {/* Outer ring border outside the pie for clear visual separation */}
                      <Pie
                        data={[{ value: 1 }]}
                        cx="50%"
                        cy="50%"
                        innerRadius="80%"
                        outerRadius="80%"
                        fill="none"
                        isAnimationActive={false}
                        stroke="#eeeeee"
                        strokeWidth={8}
                        strokeLinejoin="round"
                      />

                      {chartConfig.legend !== 'none' && (() => {
                        const computedLegendPayload = legendPayload(processedChartData, chartConfig?.dataOverrides, chartConfig)
                        return (
                          <Legend
                            payload={computedLegendPayload}
                            verticalAlign={chartConfig.legend === 'right' ? 'middle' : 'bottom'}
                            align={chartConfig.legend === 'right' ? 'right' : 'center'}
                            layout={chartConfig.legend === 'right' ? 'vertical' : 'horizontal'}
                            iconType="square"
                            iconSize={14}
                            height={20}
                            chartHeight={height}
                            content={(props) => legendContent({
                              ...props,
                              payload: computedLegendPayload,
                              dataOverrides: chartConfig?.dataOverrides,
                              chartConfig
                            })}
                            wrapperStyle={{
                              top: chartConfig.legend === 'right' && '50%',
                              transform: chartConfig.legend === 'right' && 'translateY(-50%)',
                              height: chartConfig.legend === 'right' && 'auto',
                              bottom: chartConfig.legend === 'right' ? 'auto' : '10px'
                            }}
                          />
                        )
                      })()}
                    </RePieChart>
                  </ResponsiveContainer>
                </div>

                {dataWarnMessage && <div className='chart-error'>
                  {dataWarnMessage}
                </div>
                }
              </div>
            </div>
        }
      </React.Fragment>
    )
  }

  getStrokeLine = (entry, index) => {
    const { chartConfig } = this.state

    const isCompare = chartConfig?.dataStreamSingle?.breakdownType === 'compare_streams'

    if (isCompare) {
      // Build palette from stream labels if available; fallback to alternating palette order
      const streams = chartConfig?.dataStreams?.streams || chartConfig?.dataStreamSingle?.dataStreams?.streams || []
      const used = []
      const streamPalette = streams.map((s, i) => {
        const key = s?.streamLabel?.value || s?.name || i
        const color = s?.streamLabel?.color || getThemeThenRandomSeriesColor({
          key,
          index: i,
          palette: THEME_COLOR_SWATCH,
          reservedColors: used
        })
        used.push(color)
        return color
      })
      // Compare streams: color by stream index
      return streamPalette[index] || getThemeThenRandomSeriesColor({ key: index, index, palette: THEME_COLOR_SWATCH })
    }

    // By category: use fixed workspace color order: 0, last, 1, last-1, 2, ...
    const key = entry?.metric || entry?.name || entry?.group || index
    return getThemeThenRandomSeriesColor({ key, index, palette: THEME_COLOR_SWATCH })
  }

  render() {
    const { chart, isLegacy, color, isEditMode } = this.props;

    return (
      <Card
        noGrid
        color={this.props.color}
        // title={title}
        menu={this.state.hasMenu && !isLegacy ? 'dot' : ''}
        chartId={!this.state.isChartDataLoading && (chart?.id || '66dece43216604577e3ab1a0')}
        contentLoading={this.state.isChartDataLoading}
        className={classNames("overview chart-cards", {
          "charts-card-preview": this.props.isPreview
        })}
        height={this.props.isPreview && this.props.height + 85}
        content={this.state.isChartDataLoading ? <ChartLoader color={this.props.color} /> : this.renderContent()}
        onToogleMenu={this.props.onToogleMenu}
        dropDownMenu={!this.props.isPreview && this.props.onEdit && chartDotMenu({
          color,
          showAI: this.props.showAI,
          onEdit: this.props.onEdit,
          onCopy: this.props.onCopy,
          onDelete: this.props.onDelete,
          onExplainKPI: () => this.props.onExplainKPI(chart?.id),
          onImproveChart: () => this.props.onImproveChart(chart?.id),
          isEditMode
        })}
      >
      </Card>
    );
  }
}

PieChart.propTypes = {
  onEdit: PropTypes.func,
  model: PropTypes.object,
  chart: PropTypes.any,
  previewParams: PropTypes.any,
  dashboardDevices: PropTypes.any,
  dashboardTimeRange: PropTypes.any,
  height: PropTypes.any,
  isPreview: PropTypes.bool,
  refreshPreview: PropTypes.bool,
  groupByColors: PropTypes.object
};

export default PieChart;